/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { ChannelList } from "@/components/studio/ChannelList/ChannelList";
import { ExportModal } from "@/components/studio/Export/ExportModal";
import { MessageView } from "@/components/studio/MessageView/MessageView";
import { SearchPopover } from "@/components/studio/Search/SearchPopover";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useWindowGuard } from "@/hooks/useWindowGuard";
import { useSelection } from "@/lib/stores/selection";
import type { PackageStats, ProcessedChannel } from "@/types";
import {
  ArrowLeft,
  Database,
  FileText,
  MessagesSquare,
  Paperclip,
} from "lucide-react";
import { useCallback, useState } from "react";

interface StudioProps {
  channels: ProcessedChannel[];
  stats: PackageStats;
  setCurrentScreen: (screen: "landing" | "studio") => void;
}

interface StudioState {
  selectedChannel?: ProcessedChannel;
  modals: {
    export: boolean;
    exit: boolean;
  };
}

const initialState: StudioState = {
  selectedChannel: undefined,
  modals: {
    export: false,
    exit: false,
  },
};

const buildSha = import.meta.env.VITE_GIT_SHA;

export function Studio({ channels, stats, setCurrentScreen }: StudioProps) {
  const [state, setState] = useState<StudioState>(initialState);
  const { selectedMessages, clearSelection } = useSelection();

  const totalSelections = Array.from(selectedMessages.values()).reduce(
    (total, messages) => total + messages.size,
    0,
  );

  useWindowGuard(totalSelections > 0);

  function toggleModal(modal: keyof StudioState["modals"]) {
    setState((prev) => ({
      ...prev,
      modals: {
        ...prev.modals,
        [modal]: !prev.modals[modal],
      },
    }));
  }

  const handleExit = useCallback(() => {
    if (totalSelections > 0) {
      toggleModal("exit");
    } else {
      setCurrentScreen("landing");
    }
  }, [totalSelections, setCurrentScreen]);

  const handleChannelSelect = useCallback((channel: ProcessedChannel) => {
    setState((prev) => ({
      ...prev,
      selectedChannel:
        prev.selectedChannel?.id === channel.id ? undefined : channel,
    }));
  }, []);

  return (
    <div className="bg-background flex h-screen flex-col overflow-hidden">
      <header className="bg-card sticky top-0 z-10 border-b shadow-xs">
        <div className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleExit}
              aria-label="Return to landing page"
              className="shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>

            <div className="flex min-w-0 gap-2">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-lg font-semibold">
                  Discorch Studio
                </h1>
                {buildSha && (
                  <Badge
                    variant="outline"
                    className="hidden font-mono text-[11px] md:inline-flex"
                  >
                    {buildSha}
                  </Badge>
                )}
              </div>
              <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="secondary" className="shrink-0 gap-1">
                  <Database className="h-3 w-3" />
                  <span className="truncate">
                    {channels.length.toLocaleString()}{" "}
                    {channels.length === 1 ? "channel" : "channels"}
                  </span>
                </Badge>
                <Badge variant="secondary" className="shrink-0 gap-1">
                  <MessagesSquare className="h-3 w-3" />
                  <span className="truncate">
                    {stats.totalMessages.toLocaleString()}{" "}
                    {stats.totalMessages === 1 ? "message" : "messages"}
                  </span>
                </Badge>
                <Badge variant="secondary" className="shrink-0 gap-1">
                  <Paperclip className="h-3 w-3" />
                  <span className="truncate">
                    {stats.totalAttachments.toLocaleString()}{" "}
                    {stats.totalAttachments === 1
                      ? "attachment"
                      : "attachments"}
                  </span>
                </Badge>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex shrink-0 items-center gap-2">
              <SearchPopover channels={channels} />
              <ThemeToggle />
            </div>
            {totalSelections > 0 && (
              <Button
                onClick={() => toggleModal("export")}
                className="shrink-0 gap-2 whitespace-nowrap"
              >
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">
                  Export {totalSelections.toLocaleString()}{" "}
                  {totalSelections === 1 ? "message" : "messages"}
                </span>
                <span className="sm:hidden">Export ({totalSelections})</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1 gap-4 overflow-hidden p-4">
        <ChannelList
          channels={channels}
          onChannelSelect={handleChannelSelect}
          selectedChannel={state.selectedChannel}
        />
        <MessageView
          selectedChannel={state.selectedChannel}
          onClose={() =>
            setState((prev) => ({ ...prev, selectedChannel: undefined }))
          }
        />
      </div>

      <div className="bg-card border-t px-4 py-2">
        <p className="text-muted-foreground text-sm">
          {totalSelections > 0
            ? `${totalSelections.toLocaleString()} ${totalSelections === 1 ? "message" : "messages"} selected across ${selectedMessages.size.toLocaleString()} ${selectedMessages.size === 1 ? "channel" : "channels"}`
            : "No messages selected"}
        </p>
      </div>

      <ExportModal
        open={state.modals.export}
        onClose={() => toggleModal("export")}
        channels={channels}
      />

      <AlertDialog
        open={state.modals.exit}
        onOpenChange={(open) => {
          setState((prev) => ({
            ...prev,
            modals: { ...prev.modals, exit: open },
          }));
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to exit?</AlertDialogTitle>
            <AlertDialogDescription>
              You have {totalSelections.toLocaleString()}{" "}
              {totalSelections === 1 ? "message" : "messages"} selected across{" "}
              {selectedMessages.size.toLocaleString()}{" "}
              {selectedMessages.size === 1 ? "channel" : "channels"}. Exiting
              now will lose all your selections and progress. This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                clearSelection();
                setState((prev) => ({
                  ...prev,
                  modals: { ...prev.modals, exit: false },
                }));
                setCurrentScreen("landing");
              }}
            >
              Exit anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
